import React from 'react'
import {
  Smile,
  PieChart,
  Users,
  Youtube,
  BookOpen,
  HelpCircle,
  LogOut,
  LogIn,
  Video,
  Award,
  LucideIcon,
  ShoppingBag,
  Film,
  VideoIcon,
} from 'lucide-react'
import { useAuthHeader } from '../../../hooks/header/useAuthHeader'
import { useNavigation } from '../../../hooks/header/useNavigation'
import { useHeaderState } from '../../../hooks/header/useHeaderState'
import { FreeButton } from '../../atom/FreeButton'
import { ReactComponent as StyleShotIcon } from 'svg/vector-icon.svg'
import {
  MOBILE_USER_MENU,
  MOBILE_GUEST_MENU,
} from '../../../constants/navigation'
import { UserType } from '../../../utils/common'
import { useGetMyPoint } from 'hooks/query/point/useGetMyPoint'

const HeaderMobile: React.FC = () => {
  const { userData, isLoggedIn, hasYoutubeAuthPermission, handleLogout } =
    useAuthHeader()
  const { data: myPointData, refetch: getMyPointRefetch } = useGetMyPoint()
  const { moveToPageByPath } = useNavigation()
  const { headerState, setState } = useHeaderState()

  // 아이콘 매핑
  const getIcon = (iconName: string): LucideIcon | null => {
    const iconMap: { [key: string]: any } = {
      Smile: Smile,
      PieChart: PieChart,
      Users: Users,
      Youtube: Youtube,
      BookOpen: BookOpen,
      HelpCircle: HelpCircle,
      LogOut: LogOut,
      LogIn: LogIn,
      Video: Video,
      Award: Award,
      ShoppingBag: ShoppingBag,
      Film: Film,
      StyleShot: StyleShotIcon,
    }
    return iconMap[iconName] || null
  }

  const handleHamburgerClick = () => {
    setState('isMobileMenuOpen', !headerState.isMobileMenuOpen)
  }

  const handleMenuClick = (path: string) => {
    setState('isMobileMenuOpen', false)

    if (path === 'logout') {
      handleLogout()
      return
    }

    if (path === 'blog') {
      window.open(
        'https://blog.fastseller.shop/',
        '_blank',
        'noopener,noreferrer',
      )
      return
    }

    moveToPageByPath(path)
  }

  const renderMobileMenu = () => {
    if (!headerState.isMobileMenuOpen) return null

    const menuItems = isLoggedIn ? MOBILE_USER_MENU : MOBILE_GUEST_MENU

    return (
      <div className="fixed left-0 top-0 z-[999] h-[100vh] w-full overflow-y-auto border-t bg-[#FFFFFF] pb-[200px]">
        {/* 닫기 버튼 */}
        <div
          className="mb-[18px] mt-[30px] flex justify-center"
          onClick={handleHamburgerClick}
        >
          <button>
            <img src="/assets/img/Icon_x.png" alt="x" width={24} height={24} />
          </button>
        </div>

        {/* 메뉴 아이템들 */}
        <div className="flex flex-col items-center px-[26px] py-[8px]">
          <div className="flex w-full flex-col items-center gap-[8px]">
            {menuItems.map((item, index) => {
              // 구분선
              if (item.name === 'line') {
                return (
                  <div
                    key={index}
                    className="my-[16px] h-[1px] w-full bg-[#D9DEE8]"
                  />
                )
              }

              // 자동 업로드 설정 (권한 확인)
              if (
                item.path === 'youtube-auth' &&
                isLoggedIn &&
                !hasYoutubeAuthPermission()
              ) {
                return null
              }

              // 회원가입 버튼 (비로그인 사용자)
              if (item.path === 'sign-up' && !isLoggedIn) {
                return (
                  <button
                    key={index}
                    className="flex h-[48px] w-[100px] items-center justify-center rounded-[60px] bg-[#0066FF] font-bold text-white"
                    onClick={() => handleMenuClick('sign-up')}
                  >
                    {item.name}
                  </button>
                )
              }

              // 아이콘 컴포넌트 가져오기
              const IconComponent = item.icon ? getIcon(item.icon) : null

              // 강조된 항목 (프레임 있음)
              if (item.highlighted) {
                return (
                  <button
                    key={index}
                    className="flex w-full items-center justify-center rounded-[8px] border border-[#D9DEE8] bg-[#F6F8FB] py-[16px]"
                    onClick={() => handleMenuClick(item.path)}
                  >
                    <div className="flex items-center gap-2">
                      {IconComponent && (
                        <IconComponent size={16} className="text-[#2E343D]" />
                      )}
                      <span className="font-bold text-[#2E343D]">
                        {item.name}
                      </span>
                    </div>
                  </button>
                )
              }

              // 일반 메뉴 아이템
              return (
                <button
                  key={index}
                  className="flex w-full items-center justify-center py-[16px]"
                  onClick={() => handleMenuClick(item.path)}
                >
                  <div className="flex items-center gap-2">
                    {IconComponent && (
                      <IconComponent size={16} className="text-[#2E343D]" />
                    )}
                    <span className="text-[#2E343D]">{item.name}</span>
                    {item.badge && (
                      <span className="flex h-[20px] items-center rounded-[4px] bg-[#FF8800] px-2 py-1 text-[10px] font-bold text-white">
                        {item.badge}
                      </span>
                    )}
                    {item.event && !item.badge && (
                      <span className="flex h-[20px] items-center rounded-[4px] bg-[#FF8800] px-2 py-1 text-[10px] font-bold text-white">
                        페이백
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative mx-auto my-0 flex h-[48px] w-full items-center justify-between px-4">
      {/* 로고 */}
      <button onClick={() => moveToPageByPath('home')}>
        <img
          className="y-[27px] w-[100px]"
          src="/assets/img/logo/fastcut_kr.png"
          alt="logo"
        />
      </button>
      <div className="flex gap-1">
        {userData?.userInfo && (
          <div className="flex items-center gap-1 font-bold">
            <p className="text-[14px]">내 캐시</p>

            <p className="flex items-center gap-[2px] text-[14px] text-main">
              <img
                src="/assets/img/cash.png"
                alt="check_circle"
                className="h-[13px] w-[11px]"
              />
              {myPointData.totalPoint.toLocaleString()}
            </p>
          </div>
        )}
        {/* 햄버거 메뉴 버튼 */}
        <button onClick={handleHamburgerClick}>
          <img src="/assets/img/Icon_hambuger.png" alt="hamburger" width={32} />
        </button>
      </div>

      {/* 모바일 메뉴 */}
      {renderMobileMenu()}
    </div>
  )
}

export default HeaderMobile
